import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, LinearProgress, Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useAppSelector } from '../../../hooks/useAppSelector';
import * as clientSlice from '../../../data/client.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import {
  Reservation,
  TripLeg,
  SyncedTripLeg,
  TripLegStatusLogEntry,
} from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import TripLegDetails from './TripLegDetails';
import TripLegStatusStepper from './TripLegStatusStepper';
import TripLegLiveTracking from './TripLegLiveTracking';
import TripLegActivity from './TripLegActivity';
import { Passenger } from '../../../data/models/passenger.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import reservationUtils from '@/services/reservationUtils';

const PREFIX = 'ReservationDetails';

const classes = {
  container: `${PREFIX}-container`,
  tabDetailsContainer: `${PREFIX}-tab-details-container`,
  detailsLeft: `${PREFIX}-details-left`,
  detailsRight: `${PREFIX}-details-right`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.tabDetailsContainer}`]: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
    },
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  [`& .${classes.detailsLeft}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      width: '384px',
    },
  },
  [`& .${classes.detailsRight}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      width: 'auto',
    },
  },
}));

interface ReservationDetailsProps {
  passenger?: Passenger | null;
  reservation?: Reservation;
  syncedTripLegs: {
    [id: string]: SyncedTripLeg;
  };
  legReferenceID: string;
  setLegReferenceID: Dispatch<SetStateAction<string>>;
}

const ReservationDetails = ({
  passenger,
  reservation,
  syncedTripLegs,
  legReferenceID,
  setLegReferenceID,
}: ReservationDetailsProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();
  const clientData = useAppSelector(clientSlice.selectPassengerClientData);
  const reservationTripLegs = useAppSelector(
    tripLegSlice.selectReservationTripLegs,
  );

  useEffect(() => {
    if (reservation) {
      dispatch(
        tripLegSlice.fetchReservationTripLegsAsync(
          reservation.reservationReferenceID!,
        ),
      );
    } else {
      dispatch(tripLegSlice.setReservationTripLegs(null));
      dispatch(tripLegSlice.setLoadedReservationTripLegs(false));
    }
  }, [reservation]);

  const sortedTripLegs = useMemo(() => {
    if (!reservation?.tripLegs?.length) return [];
    return reservationUtils.getSortedTripLegs(reservation.tripLegs);
  }, [reservation]);

  const tripLeg = useMemo(
    () => sortedTripLegs.find((tl) => tl.tripLegReferenceID === legReferenceID),
    [sortedTripLegs, legReferenceID],
  );
  const syncedTripLeg = useMemo(() => {
    let stl = null;
    if (syncedTripLegs && tripLeg?.tripLegReferenceID) {
      stl = syncedTripLegs[tripLeg.tripLegReferenceID] || null;
    }
    return stl;
  }, [syncedTripLegs, tripLeg]);

  const tripLegLogs = useMemo(() => {
    let logs: TripLegStatusLogEntry[] = [];
    if (reservationTripLegs && tripLeg?.tripLegReferenceID) {
      const leg = reservationTripLegs.tripLegs.find(
        (tl) => tl.tripLegReferenceID === tripLeg.tripLegReferenceID,
      );
      if (leg) {
        logs = leg.tripLegStatusLogs || [];
      }
    }
    return logs;
  }, [reservationTripLegs, tripLeg]);

  const tripLegPurpose = useMemo(() => {
    const pc = clientData?.purposeCodes?.find(
      (p) => p.purposeCodeUUID === tripLeg?.purposeCodeUUID,
    );
    return pc?.name || '';
  }, [tripLeg, clientData]);

  const handleLegChange = (_evt: React.ChangeEvent<{}>, newValue: string) => {
    setLegReferenceID(newValue);
    // Keep the selected leg in the URL so a page refresh stays on the same leg
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tripLegID', newValue);
        return next;
      },
      { replace: true },
    );
  };

  const isCurrentTripLeg = (tripLeg: TripLeg) => {
    return (
      tripLeg.status === TripLegStatus.Confirmed ||
      tripLeg.status === TripLegStatus.InTransit ||
      tripLeg.status === TripLegStatus.Onboard ||
      tripLeg.status === TripLegStatus.Onsite
    );
  };

  const hasRenderData = tripLeg && passenger && reservation;

  return (
    <StyledBox className={classes.container}>
      {reservation && (
        <>
          <Tabs
            variant="standard"
            indicatorColor="primary"
            textColor="primary"
            value={legReferenceID}
            onChange={handleLegChange}
            sx={{ px: 2 }}>
            {sortedTripLegs.map((leg, idx) => (
              <Tab
                key={leg.tripLegReferenceID}
                value={leg.tripLegReferenceID}
                label={
                  t('reservation.leg', { leg: idx + 1 }) +
                  (isCurrentTripLeg(leg)
                    ? ` (${t('reservation.current')})`
                    : '')
                }
              />
            ))}
          </Tabs>
        </>
      )}

      {!hasRenderData && <LinearProgress color="primary" />}
      {hasRenderData && (
        <>
          <Box className={classes.tabDetailsContainer}>
            <Box className={classes.detailsLeft}>
              <TripLegDetails
                tripLeg={tripLeg}
                tripLegPurpose={tripLegPurpose}
                syncedTripLeg={syncedTripLeg}
                isOnlyTripLeg={sortedTripLegs.length === 1}
                passenger={passenger}
                statusLogs={tripLegLogs}
              />

              <TripLegActivity
                tripLeg={tripLeg}
                syncedTripLeg={syncedTripLeg}
              />
            </Box>
            <Box className={classes.detailsRight}>
              <TripLegStatusStepper
                tripLeg={tripLeg}
                statusLogs={tripLegLogs}
              />
              <TripLegLiveTracking tripLeg={tripLeg} />
            </Box>
          </Box>
        </>
      )}
    </StyledBox>
  );
};

export default ReservationDetails;
